import { type Dtos } from "@inrev/common";
import {
	dataNeededQueryKey,
	type ProCon,
	type ProConRelationship,
	proConRelationshipsQueryKey,
	proConsQueryKey,
	usePendingIds,
} from "@portal/api/decisioning";
import {
	useDeleteProCon,
	useDeleteProConRelationship,
	useReorderProCons,
	useUpdateProCon,
} from "@portal/api/decisioning/admin";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { useDecisioningPanel } from "../DecisioningPanelContext";

type ProConImportance = Dtos.Decisioning.ProConImportance;
type ProConKind = Dtos.Decisioning.ProConKind;

type FactorsBoard = {
	/** Card a relation link is hovering, so that card can light up too. */
	hoveredId: string | null;
	setHoveredId: (id: string | null) => void;
	/** Optimistic items the server has not confirmed yet; their actions stay off. */
	pendingIds: ReadonlySet<string>;
	changeImportance: (proCon: ProCon, importance: ProConImportance) => void;
	retire: (proCon: ProCon) => void;
	remove: (proCon: ProCon) => void;
	removeRelationship: (id: ProConRelationship["id"]) => void;
	reorder: (kind: ProConKind, importance: ProConImportance, ids: readonly ProCon["id"][]) => void;
};

const FactorsBoardContext = createContext<FactorsBoard | null>(null);

export const useFactorsBoard = () => {
	const context = useContext(FactorsBoardContext);
	if (!context) throw new Error("useFactorsBoard must be used inside FactorsBoardProvider");
	return context;
};

/** Mutations and hover state shared by every card on the Factors tab. */
export const FactorsBoardProvider = ({ children }: { children: ReactNode }) => {
	const { accountId } = useDecisioningPanel();
	const { updateProCon } = useUpdateProCon(accountId);
	const { deleteProCon } = useDeleteProCon(accountId);
	const { deleteRelationship } = useDeleteProConRelationship(accountId);
	const { reorderProCons } = useReorderProCons(accountId);
	const [hoveredId, setHoveredId] = useState<string | null>(null);
	const pendingProConIds = usePendingIds(proConsQueryKey(accountId));
	const pendingRelationshipIds = usePendingIds(proConRelationshipsQueryKey(accountId));
	const pendingDataNeededIds = usePendingIds(dataNeededQueryKey(accountId));
	const pendingIds = useMemo(
		() => new Set([...pendingProConIds, ...pendingRelationshipIds, ...pendingDataNeededIds]),
		[pendingProConIds, pendingRelationshipIds, pendingDataNeededIds],
	);

	const changeImportance = useCallback(
		(proCon: ProCon, importance: ProConImportance) =>
			updateProCon({ id: proCon.id, changes: { importance } }),
		[updateProCon],
	);
	const retire = useCallback(
		(proCon: ProCon) => updateProCon({ id: proCon.id, changes: { retired: true } }),
		[updateProCon],
	);
	const remove = useCallback((proCon: ProCon) => deleteProCon(proCon.id), [deleteProCon]);
	const removeRelationship = useCallback(
		(id: ProConRelationship["id"]) => deleteRelationship(id),
		[deleteRelationship],
	);
	const reorder = useCallback(
		(kind: ProConKind, importance: ProConImportance, ids: readonly ProCon["id"][]) =>
			reorderProCons({ kind, importance, ids: [...ids] }),
		[reorderProCons],
	);

	const value = useMemo<FactorsBoard>(
		() => ({
			hoveredId,
			setHoveredId,
			pendingIds,
			changeImportance,
			retire,
			remove,
			removeRelationship,
			reorder,
		}),
		[hoveredId, pendingIds, changeImportance, retire, remove, removeRelationship, reorder],
	);

	return <FactorsBoardContext.Provider value={value}>{children}</FactorsBoardContext.Provider>;
};
